import {
    ADDRESS_CLAMP_TO_EDGE,
    BLEND_NORMAL,
    BoundingBox,
    CULLFACE_NONE,
    Entity,
    FILTER_LINEAR,
    Mat4,
    Mesh,
    MeshInstance,
    PIXELFORMAT_RGBA8,
    PlaneGeometry,
    Quat,
    ShaderMaterial,
    Texture,
    Vec2,
    Vec3
} from 'playcanvas';

import { Element, ElementType } from './element';
import { Events } from './events';
import { Scene } from './scene';
import { Serializer } from './serializer';
import { fragmentShader, vertexShader } from './shaders/water-shader';
import { Transform } from './transform';

const invWorld = new Mat4();
const vec = new Vec3();

let counter = 0;

// tiny fallback cubemap bound when no skybox is set, so the shader's samplerCube
// uniform always has something valid bound (its contribution is zeroed out via
// uHasEnvironmentMap when this fallback is in use)
let fallbackCubemap: Texture = null;
const getFallbackCubemap = (device: any): Texture => {
    if (!fallbackCubemap) {
        fallbackCubemap = new Texture(device, {
            width: 1,
            height: 1,
            format: PIXELFORMAT_RGBA8,
            cubemap: true,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        for (let face = 0; face < 6; face++) {
            fallbackCubemap.lock({ face }).set([0, 0, 0, 255]);
        }
        fallbackCubemap.unlock();
    }
    return fallbackCubemap;
};

type WaterParams = {
    halfExtent: number;
    amplitude: number;
    shallowColor: [number, number, number];
    deepColor: [number, number, number];
    foamIntensity: number;
};

const defaultParams = (): WaterParams => ({
    halfExtent: 10,
    amplitude: 0.25,
    shallowColor: [0.1, 0.55, 0.55],
    deepColor: [0.01, 0.08, 0.14],
    foamIntensity: 0.6
});

// An animated water plane, rendered with a custom vertex-displaced wave shader
// (src/shaders/water-shader.ts). Behaves as a first-class scene element like
// SceneObject (selectable, movable, persisted) but drives its own per-frame
// time uniform rather than a static mesh.
class Water extends Element {
    entity: Entity;
    material: ShaderMaterial;
    params: WaterParams;

    _name = '';
    _visible = true;
    _time = 0;

    localBoundStorage = new BoundingBox();
    worldBoundStorage = new BoundingBox();

    constructor(params: WaterParams, name: string) {
        super(ElementType.model);
        this.params = params;
        this._name = name;
        this.entity = new Entity(`water_${counter++}`);
    }

    build(device: any) {
        const { halfExtent } = this.params;

        const geometry = new PlaneGeometry({
            halfExtents: new Vec2(halfExtent, halfExtent),
            widthSegments: 128,
            lengthSegments: 128
        });
        const mesh = Mesh.fromGeometry(device, geometry);

        const material = new ShaderMaterial({
            uniqueName: 'water',
            vertexGLSL: vertexShader,
            fragmentGLSL: fragmentShader
        });
        material.cull = CULLFACE_NONE;
        material.blendType = BLEND_NORMAL;
        material.update();
        this.material = material;
        this.applyUniforms();

        const meshInstance = new MeshInstance(mesh, material);
        this.entity.addComponent('render', { meshInstances: [meshInstance] });
    }

    private applyUniforms() {
        const { amplitude, shallowColor, deepColor, foamIntensity } = this.params;
        this.material.setParameter('uAmplitude', amplitude);
        this.material.setParameter('uShallowColor', shallowColor);
        this.material.setParameter('uDeepColor', deepColor);
        this.material.setParameter('uFoamIntensity', foamIntensity);
        this.material.setParameter('uTime', this._time);
        this.applyEnvironmentMap();
    }

    // reflects the scene's current skybox cubemap (set by environment.ts) at
    // grazing angles; falls back to a dummy cubemap (contribution zeroed via
    // uHasEnvironmentMap) when no skybox is set
    private applyEnvironmentMap() {
        const skybox = this.scene?.app.scene.skybox;
        if (skybox) {
            this.material.setParameter('uEnvironmentMap', skybox);
            this.material.setParameter('uHasEnvironmentMap', 1);
        } else {
            this.material.setParameter('uEnvironmentMap', getFallbackCubemap(this.scene.graphicsDevice));
            this.material.setParameter('uHasEnvironmentMap', 0);
        }
    }

    get meshInstances(): MeshInstance[] {
        const result: MeshInstance[] = [];
        this.entity.findComponents('render').forEach((r: any) => result.push(...r.meshInstances));
        return result;
    }

    destroy() {
        super.destroy();
        this.entity.destroy();
    }

    add() {
        this.build(this.scene.graphicsDevice);
        this.entity.render.layers = [this.scene.worldLayer.id];
        this.scene.attachEntity(this);
        this.updateWorldBound();
    }

    remove() {
        this.scene.detachEntity(this);
        this.scene.boundDirty = true;
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.entity.getWorldTransform().data);
        serializer.pack(this.visible);
    }

    onUpdate(deltaTime: number) {
        // an ancestor group can hide the water without clearing its own `visible` flag;
        // a hidden surface must not animate or force a redraw either way
        if (!this.entity.enabled) {
            return;
        }
        this._time += deltaTime;
        this.material.setParameter('uTime', this._time);
        // picks up environment preset changes (skybox swapped/cleared) while visible
        this.applyEnvironmentMap();
        // a pure time-uniform animation doesn't register as a moved/changed element in
        // the scene's per-frame diff, so force a redraw every frame while visible.
        this.scene.forceRender = true;
    }

    focalPoint() {
        return this.worldBound.center;
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const { entity } = this;
        if (position) entity.setLocalPosition(position);
        if (rotation) entity.setLocalRotation(rotation);
        if (scale) entity.setLocalScale(scale);
        this.updateWorldBound();
        this.scene.events.fire('model.moved', this);
    }

    private updateWorldBound() {
        const instances = this.meshInstances;
        if (instances.length === 0) {
            this.scene.boundDirty = true;
            return;
        }
        this.worldBoundStorage.copy(instances[0].aabb);
        for (let i = 1; i < instances.length; ++i) {
            this.worldBoundStorage.add(instances[i].aabb);
        }
        invWorld.copy(this.entity.getWorldTransform()).invert();
        this.localBoundStorage.setFromTransformedAabb(this.worldBoundStorage, invWorld);
        this.scene.boundDirty = true;
    }

    get localBound() {
        return this.localBoundStorage;
    }

    get worldBound() {
        return this.worldBoundStorage;
    }

    get worldTransform() {
        return this.entity.getWorldTransform();
    }

    set name(value: string) {
        if (value !== this._name) {
            this._name = value;
            this.scene?.events.fire('model.name', this);
        }
    }

    get name() {
        return this._name;
    }

    set visible(value: boolean) {
        if (value !== this._visible) {
            this._visible = value;
            this.updateEffectiveVisibility();
            this.scene?.events.fire('model.visibility', this);
        }
    }

    get visible() {
        return this._visible;
    }

    // Rendering depends on this element AND every ancestor group being visible, so hiding
    // a group hides its contents without destroying each child's own visibility state.
    // Guarded for the window before scene.add(), when there is no scene to ask.
    updateEffectiveVisibility() {
        this.entity.enabled = this.scene ? this.scene.effectiveVisibility(this) : this._visible;
    }

    getPivot(mode: 'center' | 'boundCenter', selection: boolean, result: Transform) {
        const { entity } = this;
        switch (mode) {
            case 'center':
                result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
                break;
            case 'boundCenter':
                entity.getLocalTransform().transformPoint(this.localBound.center, vec);
                result.set(vec, entity.getLocalRotation(), entity.getLocalScale());
                break;
        }
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const pack4 = (q: Quat) => [q.x, q.y, q.z, q.w];
        return {
            name: this.name,
            position: pack3(this.entity.getLocalPosition()),
            rotation: pack4(this.entity.getLocalRotation()),
            scale: pack3(this.entity.getLocalScale()),
            visible: this.visible,
            params: this.params
        };
    }

    docDeserialize(doc: any) {
        const { name, position, rotation, scale, visible, params } = doc;
        this.name = name;
        if (params) {
            this.params = { ...defaultParams(), ...params };
            this.applyUniforms();
        }
        this.move(new Vec3(position), new Quat(rotation), new Vec3(scale));
        this.visible = visible;
    }
}

// factory: create a water element with default params
const createWater = (scene: Scene, name: string): Water => {
    return new Water(defaultParams(), name);
};

const registerWaterEvents = (events: Events, scene: Scene) => {
    events.function('docSerialize.water', (): any[] => {
        const waters = scene.elements.filter(e => e instanceof Water) as Water[];
        return waters.map(w => w.docSerialize());
    });

    events.function('docDeserialize.water', async (docs: any[] = []) => {
        for (const doc of docs) {
            const water = createWater(scene, doc.name ?? 'Water');
            await scene.add(water);
            water.docDeserialize(doc);
        }
    });
};

export { Water, createWater, WaterParams, registerWaterEvents };
